import { apiClient } from "@/services/api"
import { defineStore } from "pinia"

// The request currently in flight, shared by every consumer. Contact positions are pure lookup data
// that four components need at the same time — the customer contacts tab alone mounts the table and
// one modal input per contact — and each of them used to fire its own GET on mount.
let inFlight = null

export const useCustomerContactPositionStore = defineStore("customerContactPosition", {
  state: () => ({
    positions: [],
    // A load attempt has finished, successfully or not. Until it has, an id that resolves to nothing
    // only means "not fetched yet"; afterwards it means the position is really gone. Callers need to
    // tell those apart before they label anything as unknown.
    loaded: false,
    failed: false,
  }),
  getters: {
    getPositions: state => state.positions,
    isLoaded: state => state.loaded,
    /**
     * The name behind a position reference, or `null` when it cannot be resolved.
     *
     * Deliberately never falls back to the id. It is a technical value that means nothing to the
     * user, and the whole point of the reference is that the label lives in exactly one place —
     * printing the id instead is how a missing lookup used to leak into the UI.
     */
    getPositionName: state => reference => {
      if (!reference) return null
      // Some callers hold a whole position object (a working copy in a form) rather than an id.
      if (typeof reference === "object") return reference.name || null
      return state.positions.find(position => position.id === reference)?.name ?? null
    },
  },
  actions: {
    setPositions(positions) {
      this.positions = [...positions]
    },
    setPosition(position) {
      const index = this.positions.findIndex(pos => pos.id === position.id)
      this.positions[index].name = position.name
    },
    /**
     * Drop the cached list. Called on sign-out: positions belong to a workspace, and since the list
     * is now fetched once per session rather than on every mount, the next workspace would otherwise
     * keep resolving its ids against the previous one's names.
     */
    reset() {
      inFlight = null
      this.$reset()
    },
    /**
     * Load the list once for the whole app; retry only when the previous attempt failed.
     *
     * A failed load leaves the list empty, which looks exactly like "this workspace has no positions"
     * — every reference then stays unresolved for as long as the page lives. Retrying on the next
     * mount costs nothing and gets the names back.
     */
    ensureLoaded() {
      if (inFlight) return inFlight
      if (this.loaded && !this.failed) return Promise.resolve()

      inFlight = this.loadPositions().finally(() => {
        inFlight = null
      })
      return inFlight
    },
    updatePosition({ id, position }) {
      return apiClient
        .put(`/customer/contact/position/${id}`, position, { headers: { tenant: true, authorization: true } })
        .then(res => {
          if (res.status === 200) {
            this.setPosition(res.data)
          }
          return res
        })
    },
    createPosition(position) {
      return apiClient
        .post("/customer/contact/position", position, { headers: { tenant: true, authorization: true } })
        .then(res => res)
    },
    loadPositions() {
      return (
        apiClient
          .get("/customer/contact/position", { headers: { tenant: true, authorization: true } })
          .then(res => {
            if (res.status === 200) {
              this.setPositions(res.data)
              this.failed = false
            }
            return res
          })
          // Nothing here can be recovered from at the call site — every consumer only reads the list —
          // so the failure is recorded for `ensureLoaded` to retry instead of rejecting into nowhere.
          .catch(() => {
            this.failed = true
          })
          .finally(() => {
            this.loaded = true
          })
      )
    },
    deletePosition(id) {
      return (
        apiClient
          .delete(`/customer/contact/position/${id}`, { headers: { tenant: true, authorization: true } })
          .then(res => res)
          // The response interceptor raises the violation now; the caller still inspects the status.
          .catch(err => err.response)
      )
    },
  },
})
